import React, { useMemo, useState } from 'react';
import { DatePicker, Button, Tooltip, Tag } from 'antd';
import moment, { Moment } from 'moment';
import { useDispatch } from 'react-redux';
import { requestTrigger } from 'store/admin/dump/slice';
import { useTranslation } from 'react-i18next';
import { ExportFolder } from '../../types';

const { MonthPicker } = DatePicker;

type Props = {
  folders: ExportFolder[] | null;
  loading?: boolean;
};

export const TriggerByMonth: React.FC<Props> = ({ folders, loading }) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState<Moment | null>(null);
  const { t } = useTranslation();

  const completedSet = useMemo(() => {
    const set = new Set<string>();
    (folders || []).forEach((f) => {
      if (f.exportStatus === 'COMPLETED') set.add(f.name);
    });
    return set;
  }, [folders]);

  const toYYYYMM = (m: Moment) => m.format('YYYYMM');
  const toDashed = (m: Moment) => m.format('YYYY-MM');

  const disabledDate = (current: Moment) => {
    if (!current) return false;
    return completedSet.has(toYYYYMM(current));
  };

  const isSelectedCompleted = value && completedSet.has(toYYYYMM(value));
  const canTrigger = !!value && !isSelectedCompleted;

  const onTrigger = () => {
    if (!value) return;
    dispatch(requestTrigger({ period: toDashed(value) }));
  };

  const tooltipTitle = useMemo(() => {
    if (isSelectedCompleted) return t('dump.errors.selectedCompleted');
    if (!value) return t('dump.generate.toSelect');
    return t('dump.generate.generate');
  }, [isSelectedCompleted, value]);

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <MonthPicker
        value={value as any}
        onChange={(val) => setValue(val)}
        disabledDate={disabledDate}
        allowClear
        placeholder={t('dump.generate.selectMonth')}
        format="YYYY-MM"
      />

      <Tooltip title={tooltipTitle}>
        <Button type="primary" onClick={onTrigger} disabled={!canTrigger} loading={loading}>
          {t('dump.generate.generationButton')}
        </Button>
      </Tooltip>

      <div style={{ marginLeft: 8 }}>
        <Tag color="success">{t('dump.generate.tag')}</Tag>
      </div>
    </div>
  );
};

export default TriggerByMonth;
